import { z } from "zod";

import { STORAGE_TIERS, STORAGE_TIER_IDS, tierCapacityLabel } from "./storage-tiers.js";

export const USE_CASES = [
  { id: "training", label: "Training" },
  { id: "fine_tuning", label: "Fine-tuning" },
  { id: "inference", label: "Inference" },
  { id: "research", label: "Research and experimentation" },
  { id: "other", label: "Other" },
] as const;

export const SERVICE_OFFERINGS = [
  { id: "managed_os_bare_metal", label: "Bare metal, Mantle-managed OS" },
  { id: "customer_os_bare_metal", label: "Bare metal, customer-managed OS" },
  { id: "llm_inference", label: "LLM inference" },
  { id: "managed_kubernetes", label: "Managed Kubernetes" },
  { id: "managed_slurm", label: "Managed Slurm" },
  { id: "other", label: "Other" },
] as const;

// GB200 capacity is sized off the node as the atomic unit. Everything the
// customer sees (GPUs, Grace CPUs, racks) is derived from a node count.
export const GPUS_PER_NODE = 4;
export const GRACE_CPUS_PER_NODE = 2;
export const NODES_PER_RACK = 18;
export const MIN_NODES = 3;

/**
 * Node count derived from a GPU count (a node is 4 GPUs), floored at the
 * minimum. Rounds up so a requested GPU count is never silently under-delivered
 * (e.g. 13 GPUs resolves to 4 nodes / 16 GPUs, not 3 nodes / 12 GPUs).
 */
export function nodesFromGpus(gpus: number): number {
  return Math.max(MIN_NODES, Math.ceil(gpus / GPUS_PER_NODE));
}

/** Node count derived from a rack count (a rack is 18 nodes), floored at the minimum. */
export function nodesFromRacks(racks: number): number {
  return Math.max(MIN_NODES, Math.round(racks * NODES_PER_RACK));
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b);
}

/**
 * Rack count for a node count as a reduced fraction (a rack is 18 nodes), e.g.
 * 3 -> "1/6", 9 -> "1/2", 18 -> "1", 24 -> "1 1/3". Racks are indivisible units
 * of 18 nodes, so sub-rack orders read as fractions rather than ugly decimals.
 */
export function rackFraction(nodeCount: number): string {
  const whole = Math.floor(nodeCount / NODES_PER_RACK);
  const remainder = nodeCount % NODES_PER_RACK;
  if (remainder === 0) return String(whole);
  const divisor = greatestCommonDivisor(remainder, NODES_PER_RACK);
  const fraction = `${remainder / divisor}/${NODES_PER_RACK / divisor}`;
  return whole === 0 ? fraction : `${whole} ${fraction}`;
}

function formatRacks(nodeCount: number): string {
  const unit = nodeCount > NODES_PER_RACK ? "racks" : "rack";
  return `${rackFraction(nodeCount)} ${unit}`;
}

/** Human-readable label for a workload-interconnect id, for summaries and Slack. */
export function interconnectLabel(id: string | undefined): string {
  return INTERCONNECT_OPTIONS.find((option) => option.id === id)?.label ?? "Not specified";
}

/** Human-readable capacity summary shown in the UI review and the GTM Slack post. */
export function describeCapacity(nodeCount: number): string {
  const gpus = nodeCount * GPUS_PER_NODE;
  const cpus = nodeCount * GRACE_CPUS_PER_NODE;
  return `${nodeCount} trays · ${gpus} Blackwell GPUs · ${cpus} Grace CPUs · ${formatRacks(nodeCount)}`;
}

/** True when a bare-metal (infrastructure-track) service is selected. */
export function hasInfrastructure(answers: CapacityRequestAnswers): boolean {
  return (
    answers.services?.some((service) =>
      ["managed_os_bare_metal", "customer_os_bare_metal"].includes(service),
    ) ?? false
  );
}

/** True when the LLM inference service is selected. */
export function hasInference(answers: CapacityRequestAnswers): boolean {
  return answers.services?.includes("llm_inference") ?? false;
}

export const TIMELINES = [
  { id: "asap", label: "As soon as possible" },
  { id: "under_1_month", label: "Under 1 month" },
  { id: "1_to_3_months", label: "1 to 3 months" },
  { id: "over_3_months", label: "More than 3 months" },
] as const;

export const TERM_LENGTHS = [
  { id: "weeks", label: "Weeks" },
  { id: "months", label: "Months" },
  { id: "1_year", label: "1 year" },
  { id: "multi_year", label: "Multi-year" },
] as const;

export const INTERCONNECT_OPTIONS = [
  { id: "single_node", label: "Single tray at a time" },
  { id: "nvlink_domain", label: "Multi-tray within one rack (NVLink domain)" },
  { id: "multi_rack", label: "Multi-rack distributed (scale-out fabric)" },
] as const;

export const BANDWIDTH_OPTIONS = [
  { id: "under_1_gbps", label: "Under 1 Gbps" },
  { id: "1_to_10_gbps", label: "1 to 10 Gbps" },
  { id: "over_10_gbps", label: "More than 10 Gbps" },
] as const;

export const ACCESS_METHODS = [
  { id: "ssh_jump_host", label: "SSH jump host" },
  { id: "vpn_tunnel", label: "VPN tunnel" },
  { id: "public_node_ips", label: "Public IPs on trays" },
] as const;

// For customer-managed OS bare metal, the customer boots their own OS via an
// iPXE menu Mantle chainloads. The one intake-relevant choice is where those
// boot artifacts are served from (it drives the tenant-storage add-on and the
// install-time bandwidth expectation).
export const BOOT_ARTIFACT_SOURCES = [
  { id: "own_boot_server", label: "Our own internet-reachable boot server" },
  { id: "mantle_tenant_storage", label: "Mantle-hosted tenant storage (NFS/HTTP)" },
  { id: "undecided", label: "Undecided / need guidance" },
] as const;

export const INFERENCE_MODES = [
  { id: "realtime", label: "Real-time" },
  { id: "batch", label: "Batch" },
] as const;

export const TRAFFIC_PATTERNS = [
  { id: "steady", label: "Steady" },
  { id: "bursty", label: "Bursty" },
] as const;

export interface CapacityRequestSummaryField {
  label: string;
  value: string;
}

/** The contact + answer data a submission summary (Slack/email) is built from. */
export interface CapacityRequestSummaryInput {
  company: string | null;
  email: string;
  contactName: string | null;
  role: string | null;
  answers: CapacityRequestAnswers;
}

const SUMMARY_NOT_SPECIFIED = "Not specified";

function summaryLabel(
  options: readonly { id: string; label: string }[],
  id: string | undefined,
): string | undefined {
  if (!id) return undefined;
  return options.find((option) => option.id === id)?.label ?? id;
}

function summaryMultiLabel(
  options: readonly { id: string; label: string }[],
  ids: readonly string[] | undefined,
): string | undefined {
  if (!ids?.length) return undefined;
  return ids.map((id) => summaryLabel(options, id) ?? id).join(", ");
}

// Retired-option label maps. These describe questions removed from the active
// form but kept read-only so a resumed pre-rework draft doesn't lose captured
// answers. Shared so the staff/on-screen summary and the submission email label
// them identically instead of drifting.
export const LEGACY_CONNECTION_METHODS: Record<string, string> = {
  site_to_site_ipsec: "Site-to-site IPsec",
  ssh_public_ip: "SSH over a public IP",
  direct_peering: "Direct peering or cross-connect",
  other: "Other",
};
export const LEGACY_HANDOFF: Record<string, string> = {
  raw_hardware: "Raw hardware",
  base_image: "Pre-provisioned base image",
};
export const LEGACY_PROVISIONING: Record<string, string> = {
  customer_pxe: "Customer PXE or iPXE",
  iso: "ISO",
  mantle_seeded: "Mantle seeds a base image; customer manages it",
  other: "Other",
};
export const LEGACY_OOB: Record<string, string> = {
  redfish: "Redfish API",
  serial_console: "Serial over LAN console",
};

/**
 * Retired "How you'll connect" answer, present only on drafts saved before that
 * question was removed. Returned so both summaries can surface the preserved
 * value; undefined when the draft never captured one.
 */
export function legacyConnectionField(
  answers: CapacityRequestAnswers,
): CapacityRequestSummaryField | undefined {
  const infrastructure = answers.infrastructure;
  const methods = infrastructure?.connectionMethods?.filter((id) => typeof id === "string") ?? [];
  if (!methods.length) return undefined;
  const labels = methods.map((id) => LEGACY_CONNECTION_METHODS[id] ?? id).join(", ");
  const other = infrastructure?.otherConnectionMethod;
  return {
    label: "Connection",
    value: methods.includes("other") && other ? `${labels} (${other})` : labels,
  };
}

/**
 * Retired customer-managed-OS answers (handoff / provisioning method / out-of-band
 * access) preserved on pre-rework drafts. Returned so both summaries render the
 * same read-only rows instead of silently dropping submitted intake data.
 */
export function legacyCustomerOsFields(
  answers: CapacityRequestAnswers,
): CapacityRequestSummaryField[] {
  const os = answers.customerManagedOs;
  if (!os) return [];
  const fields: CapacityRequestSummaryField[] = [];
  if (os.handoff) {
    fields.push({ label: "Handoff", value: LEGACY_HANDOFF[os.handoff] ?? os.handoff });
  }
  if (os.provisioningMethod) {
    const base = LEGACY_PROVISIONING[os.provisioningMethod] ?? os.provisioningMethod;
    fields.push({
      label: "Provisioning method",
      value:
        os.provisioningMethod === "other" && os.otherProvisioningMethod
          ? `${base} (${os.otherProvisioningMethod})`
          : base,
    });
  }
  const oob = os.outOfBandAccess?.filter((id) => typeof id === "string") ?? [];
  if (oob.length) {
    fields.push({
      label: "Out-of-band access",
      value: oob.map((id) => LEGACY_OOB[id] ?? id).join(", "),
    });
  }
  return fields;
}

/**
 * Readable label/value pairs describing a submitted capacity request, grouped in
 * the same order as the on-screen review summary. Shared so the submission email
 * and any other server-side summary render one source of truth.
 */
export function capacityRequestSummaryFields(
  request: CapacityRequestSummaryInput,
): CapacityRequestSummaryField[] {
  const { answers } = request;
  const fields: CapacityRequestSummaryField[] = [];
  const add = (label: string, value: string | number | undefined) => {
    const normalized = value === undefined || value === "" ? SUMMARY_NOT_SPECIFIED : String(value);
    fields.push({ label, value: normalized });
  };

  add("Company", request.company ?? undefined);
  const contact = request.contactName
    ? request.role
      ? `${request.contactName} (${request.role})`
      : request.contactName
    : undefined;
  add("Contact", contact);
  add("Email", request.email);

  const useCases = summaryMultiLabel(USE_CASES, answers.useCases);
  add(
    "Use cases",
    answers.useCases?.includes("other") && answers.otherUseCase
      ? `${useCases} (${answers.otherUseCase})`
      : useCases,
  );

  const services = summaryMultiLabel(SERVICE_OFFERINGS, answers.services);
  add(
    "Services",
    answers.services?.includes("other") && answers.otherService
      ? `${services} (${answers.otherService})`
      : services,
  );

  add(
    "Capacity",
    answers.nodeCount === undefined ? undefined : describeCapacity(answers.nodeCount),
  );
  add("Timeline", summaryLabel(TIMELINES, answers.timeline));
  add("Term length", summaryLabel(TERM_LENGTHS, answers.termLength));

  // Gate the branch sections on the selected service (matching the on-screen
  // review summary), not on whether a nested answer blob happens to exist. A
  // draft can retain infrastructure/inference answers from a service the
  // prospect later deselected; submit validation doesn't strip them, so keying
  // off the blob would email rows the review UI hides.
  const managedOsSelected = answers.services?.includes("managed_os_bare_metal") ?? false;
  const customerOsSelected = answers.services?.includes("customer_os_bare_metal") ?? false;

  if (hasInfrastructure(answers)) {
    const infrastructure = answers.infrastructure ?? {};
    add("Workload interconnect", summaryLabel(INTERCONNECT_OPTIONS, infrastructure.interconnect));
    const connection = legacyConnectionField(answers);
    if (connection) fields.push(connection);
    add("Bandwidth", summaryLabel(BANDWIDTH_OPTIONS, infrastructure.bandwidth));
    const tier = infrastructure.storageTier
      ? STORAGE_TIERS.find((option) => option.id === infrastructure.storageTier)
      : undefined;
    add(
      "High-performance storage",
      tier ? `${tier.name} (${tierCapacityLabel(tier)})` : infrastructure.storageTier,
    );
    add("Access methods", summaryMultiLabel(ACCESS_METHODS, infrastructure.accessMethods));
    if (infrastructure.accessMethods?.includes("public_node_ips")) {
      add("Public IPs", infrastructure.publicIpCount);
    }
  }

  if (managedOsSelected) {
    add("Managed-OS additional requirements", answers.managedOs?.additionalRequirements);
  }

  if (customerOsSelected) {
    add(
      "OS / boot artifacts served from",
      summaryLabel(BOOT_ARTIFACT_SOURCES, answers.customerManagedOs?.bootArtifactSource),
    );
    for (const field of legacyCustomerOsFields(answers)) fields.push(field);
  }

  if (hasInference(answers)) {
    const inference = answers.inference ?? {};
    add("Models", inference.models);
    add("Serving type", summaryLabel(INFERENCE_MODES, inference.mode));
    add("Traffic pattern", summaryLabel(TRAFFIC_PATTERNS, inference.trafficPattern));
  }

  const legal = answers.legal;
  add("US eligibility attested", legal?.eligibilityAttested ? "Yes" : undefined);
  add(
    "Additional diligence acknowledged",
    legal?.additionalDiligenceAcknowledged ? "Yes" : undefined,
  );

  return fields;
}

export interface ManagedStackComponent {
  name: string;
  version?: string;
}

// Customer-facing Mantle-managed OS baseline. Version-less on purpose: the DGX OS
// image bundles the validated GPU driver / CUDA / NCCL, and pinning versions here
// would drift from the image. See the memex repo for the underlying platform docs.
export const MANAGED_OS_STACK: readonly ManagedStackComponent[] = [
  { name: "NVIDIA DGX OS (Ubuntu 24.04 LTS, ARM64)" },
  { name: "NVIDIA GPU driver, CUDA, and NCCL (DGX-validated)" },
  { name: "containerd with the NVIDIA Container Toolkit" },
  { name: "Slurm and/or Kubernetes (optional)" },
];

function ids<const T extends readonly { id: string }[]>(values: T) {
  return values.map(({ id }) => id) as [T[number]["id"], ...T[number]["id"][]];
}

/**
 * Array of enum values that silently drops members no longer in the catalog.
 * A draft saved before an option was retired (e.g. a removed service) would
 * otherwise fail strict enum validation on resume and block autosave/submit;
 * dropping the stale ids lets the prospect continue.
 */
function tolerantEnumArray<T extends z.ZodTypeAny>(element: T, allowed: readonly string[]) {
  const known = new Set<string>(allowed);
  return z.preprocess(
    (input) => (Array.isArray(input) ? input.filter((value) => known.has(value as string)) : input),
    z.array(element),
  );
}

const useCaseSchema = z.enum(ids(USE_CASES));
const serviceOfferingSchema = z.enum(ids(SERVICE_OFFERINGS));
const timelineSchema = z.enum(ids(TIMELINES));
const termLengthSchema = z.enum(ids(TERM_LENGTHS));
const interconnectSchema = z.enum(ids(INTERCONNECT_OPTIONS));
const bandwidthSchema = z.enum(ids(BANDWIDTH_OPTIONS));
const accessMethodSchema = z.enum(ids(ACCESS_METHODS));
const bootArtifactSourceSchema = z.enum(ids(BOOT_ARTIFACT_SOURCES));
const inferenceModeSchema = z.enum(ids(INFERENCE_MODES));
const trafficPatternSchema = z.enum(ids(TRAFFIC_PATTERNS));
const storageTierSchema = z.enum(STORAGE_TIER_IDS);

const infrastructureAnswersSchema = z.preprocess(
  // Drafts saved before the infra consolidation stored a standalone
  // `publicIpsNeeded` boolean separate from `accessMethods`. That field no longer
  // exists, so preserve the prospect's intent by selecting the public-IP access
  // method when resuming such a draft; otherwise the "public IPs" signal (and its
  // count) would silently drop.
  (input) => {
    if (!input || typeof input !== "object" || Array.isArray(input)) return input;
    const raw = input as Record<string, unknown>;
    if (raw.publicIpsNeeded !== true) return input;
    const methods = Array.isArray(raw.accessMethods) ? (raw.accessMethods as unknown[]) : [];
    if (methods.includes("public_node_ips")) return input;
    return { ...raw, accessMethods: [...methods, "public_node_ips"] };
  },
  z.object({
    interconnect: interconnectSchema.optional(),
    bandwidth: bandwidthSchema.optional(),
    accessMethods: tolerantEnumArray(accessMethodSchema, ids(ACCESS_METHODS)).optional(),
    publicIpCount: z.number().int().positive().optional(),
    storageTier: storageTierSchema.optional(),
    // Deprecated: the "How you'll connect" question was removed. Kept as
    // loosely-typed optionals so a resumed pre-removal draft doesn't lose the
    // captured answers on normalize/autosave; shown read-only in the staff summary.
    connectionMethods: z.array(z.string()).optional(),
    otherConnectionMethod: z.string().optional(),
  }),
);

const managedOsAnswersSchema = z.object({
  additionalRequirements: z
    .string()
    .trim()
    .max(5_000, "Keep this under 5,000 characters.")
    .optional(),
});

const customerManagedOsAnswersSchema = z.object({
  bootArtifactSource: bootArtifactSourceSchema.optional(),
  // Deprecated: the module previously asked handoff / provisioning-method /
  // out-of-band access. Kept as loosely-typed optionals (no enum) so a resumed
  // pre-rework draft doesn't lose those captured answers when normalizeDraftAnswers
  // reparses and autosave rewrites the blob. Not surfaced in the active form; shown
  // read-only in the staff summary via LegacyCustomerOsRows.
  handoff: z.string().optional(),
  provisioningMethod: z.string().optional(),
  otherProvisioningMethod: z.string().optional(),
  outOfBandAccess: z.array(z.string()).optional(),
});

const inferenceAnswersSchema = z.object({
  models: z.string().trim().max(1_000, "Keep the model list under 1,000 characters.").optional(),
  mode: inferenceModeSchema.optional(),
  trafficPattern: trafficPatternSchema.optional(),
});

const legalAnswersSchema = z.object({
  eligibilityAttested: z.boolean().optional(),
  additionalDiligenceAcknowledged: z.boolean().optional(),
});

/**
 * Saveable draft answers. Fields stay optional because prospects can leave and
 * resume at any point. The submit endpoint will apply branch-aware requirements.
 */
export const capacityRequestAnswersSchema = z.object({
  useCases: tolerantEnumArray(useCaseSchema, ids(USE_CASES)).optional(),
  otherUseCase: z
    .string()
    .trim()
    .max(500, "Keep your use-case description under 500 characters.")
    .optional(),
  services: tolerantEnumArray(serviceOfferingSchema, ids(SERVICE_OFFERINGS)).optional(),
  otherService: z
    .string()
    .trim()
    .max(500, "Keep your service description under 500 characters.")
    .optional(),
  // Stays lenient so a resumable draft can hold an in-progress value; the
  // MIN_NODES floor is enforced at submit (see the submission schema).
  nodeCount: z.number().int().positive().optional(),
  timeline: timelineSchema.optional(),
  termLength: termLengthSchema.optional(),
  infrastructure: infrastructureAnswersSchema.optional(),
  managedOs: managedOsAnswersSchema.optional(),
  customerManagedOs: customerManagedOsAnswersSchema.optional(),
  inference: inferenceAnswersSchema.optional(),
  legal: legalAnswersSchema.optional(),
});

const INFRASTRUCTURE_SERVICES: readonly ServiceOffering[] = [
  "managed_os_bare_metal",
  "customer_os_bare_metal",
];

function required(ctx: z.RefinementCtx, path: (string | number)[], message: string): void {
  ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
}

export const capacityRequestSubmissionSchema = z
  .object({
    company: z
      .string()
      .trim()
      .min(1, "Enter your company.")
      .max(200, "Company must be 200 characters or fewer."),
    contactName: z
      .string()
      .trim()
      .min(1, "Enter your full name.")
      .max(200, "Your name must be 200 characters or fewer."),
    role: z
      .string()
      .trim()
      .min(1, "Enter your role.")
      .max(200, "Your role must be 200 characters or fewer."),
    answers: capacityRequestAnswersSchema,
  })
  .superRefine(({ answers }, ctx) => {
    if (!answers.useCases?.length) {
      required(ctx, ["answers", "useCases"], "Select at least one use case.");
    }
    if (answers.useCases?.includes("other") && !answers.otherUseCase) {
      required(ctx, ["answers", "otherUseCase"], "Describe the use case.");
    }
    if (!answers.services?.length) required(ctx, ["answers", "services"], "Select a service.");
    if (answers.services?.includes("other") && !answers.otherService) {
      required(ctx, ["answers", "otherService"], "Describe the service.");
    }
    if (answers.nodeCount === undefined) {
      required(ctx, ["answers", "nodeCount"], "Enter how much capacity you need.");
    } else if (answers.nodeCount < MIN_NODES) {
      required(ctx, ["answers", "nodeCount"], `The minimum order is ${MIN_NODES} GB200 trays.`);
    }
    if (!answers.timeline) required(ctx, ["answers", "timeline"], "Select a timeline.");
    if (!answers.termLength) required(ctx, ["answers", "termLength"], "Select a term length.");

    const services = answers.services ?? [];
    if (services.some((service) => INFRASTRUCTURE_SERVICES.includes(service))) {
      const infrastructure = answers.infrastructure;
      if (!infrastructure?.interconnect) {
        required(
          ctx,
          ["answers", "infrastructure", "interconnect"],
          "Select a workload interconnect.",
        );
      }
      if (!infrastructure?.bandwidth) {
        required(ctx, ["answers", "infrastructure", "bandwidth"], "Select bandwidth.");
      }
      if (!infrastructure?.accessMethods?.length) {
        required(ctx, ["answers", "infrastructure", "accessMethods"], "Select an access method.");
      }
      if (
        infrastructure?.accessMethods?.includes("public_node_ips") &&
        infrastructure.publicIpCount === undefined
      ) {
        required(
          ctx,
          ["answers", "infrastructure", "publicIpCount"],
          "Enter the number of public IPs.",
        );
      }
      if (!infrastructure?.storageTier) {
        required(ctx, ["answers", "infrastructure", "storageTier"], "Select a storage tier.");
      }
    }

    // Customer-managed OS bare metal has no required intake fields: the customer
    // brings and boots their own OS via a Mantle-chainloaded iPXE menu, so the
    // module is explanatory plus one optional boot-artifact-source question.

    if (services.includes("llm_inference")) {
      const inference = answers.inference;
      if (!inference?.models) {
        required(ctx, ["answers", "inference", "models"], "List the models you plan to serve.");
      }
      if (!inference?.mode) {
        required(ctx, ["answers", "inference", "mode"], "Select real-time or batch.");
      }
      if (!inference?.trafficPattern) {
        required(ctx, ["answers", "inference", "trafficPattern"], "Select a traffic pattern.");
      }
    }

    if (answers.legal?.eligibilityAttested !== true) {
      required(
        ctx,
        ["answers", "legal", "eligibilityAttested"],
        "The eligibility attestation is required.",
      );
    }
    if (answers.legal?.additionalDiligenceAcknowledged !== true) {
      required(
        ctx,
        ["answers", "legal", "additionalDiligenceAcknowledged"],
        "The additional-diligence acknowledgment is required.",
      );
    }
  });

export type CapacityRequestAnswers = z.infer<typeof capacityRequestAnswersSchema>;

/**
 * Normalizes stored draft answers so a resumed draft can't carry values that
 * are no longer valid (e.g. a retired option) into the UI or a save/submit.
 * Falls back to the raw answers if the draft can't be parsed.
 */
export function normalizeDraftAnswers(answers: unknown): CapacityRequestAnswers {
  const result = capacityRequestAnswersSchema.safeParse(answers ?? {});
  return result.success ? result.data : ((answers ?? {}) as CapacityRequestAnswers);
}
export type ServiceOffering = (typeof SERVICE_OFFERINGS)[number]["id"];
export type CapacityRequestSubmission = z.infer<typeof capacityRequestSubmissionSchema>;
